/**
 * The `/aggregator/*` route reservations v2 calls (RESERVATIONS_V2_PLAN.md §4.5, §4.6, decision 49), held to the
 * backend snapshot by `api/routes.test.ts`. It lists the partner outlets a branch has set up (Zomato, Swiggy,
 * EazyDiner, …), which is how v2 knows which partners to offer.
 *
 * The `Wire…` types are the backend's own shapes, as read from its code
 * (`docs/architecture/reservations/backend.md` §9.1), not v2's domain types, so `api/` imports nothing from
 * `reservations/`.
 */
import { gated } from "../../capabilities/access";
import { defineRoutes, route } from "../route";

/**
 * The body v1 sends to list outlets, and the only body v2 sends. `aggregatorType: "Order"` is the one value every
 * provider supports (Zomato supports only `Order`), so the list holds every partner's outlets and v2 reads each
 * outlet's own `reservation` flag instead of asking the backend to narrow by type.
 */
export interface WireOutletFilter {
  /** the aggregator kind to list; `"Order"` because Zomato supports no other */
  aggregatorType: "Order";
  /** `"current"` lists the outlets of the branch the session is on */
  branches: "current";
  /** asks for the newer outlet format, as v1 does */
  isNew: true;
}

/**
 * One outlet row of the answer. Only the fields reservations reads are typed (the backend maps many integration
 * fields too, which stay under the index signature).
 */
export interface WireOutlet {
  /** the outlet's id */
  id?: string;
  /** the branch the outlet belongs to */
  brid?: string;
  /** the aggregator's key, such as `zomato` or `swiggydinein` */
  type?: string;
  /** whether the outlet is switched on, as the TEXT `"1"` or `"0"` (the answer mapper turns the stored boolean into text) */
  active?: string;
  /** whether the outlet takes reservations; sent only when true, so absent otherwise */
  reservation?: boolean;
  /** any other outlet field the backend returns */
  [field: string]: unknown;
}

/** The outlet filter's answer: the providers the business could use, and the outlets it has set up. */
export interface WireOutletList {
  /** the provider descriptions the backend adds beside the outlets; reservations does not read them */
  metaProviders?: unknown[];
  /** the branch's outlets, one row each */
  providers?: WireOutlet[];
}

/** The one `/aggregator/*` route reservations v2 calls: the branch's partner outlets. */
export const AGGREGATOR = defineRoutes("aggregator", {
  /** the branch's aggregator outlets, each with its on/off and reservation flags */
  configurationFilter: route<WireOutletFilter, WireOutletList>(
    "POST",
    "/aggregator/configuration/filter",
    gated({ modules: ["Account", "Sale", "Support", "Settings"] }),
    {
      notes:
        "lists only providers of the business's country that are allowed and support `aggregatorType`; `dotpe` is not a provider here, so its outlets never appear; `active` is the text `\"1\"` or `\"0\"` and `reservation` is absent when false (backend.md §9.1; aggregator_controller.js:29-34, :135-200, :407-488)",
    },
  ),
});
